import {
  Popover,
  PopoverContent,
  PlainButton,
  Heading,
  Stack,
  InlineStack,
  Text,
  Badge,
  Link,
} from '@customerio/pluma-components/react';
import { useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import {
  HealthScoreGauge,
  useWorkspaceHealthSimulation,
  useEnvHealth,
  getHealthSummary,
} from '@/features/workspace-health';
import { PlumaButton as ButtonCss } from '@customerio/pluma-components/css/components';
import clsx from 'clsx';
import { getWorkspaceIdFromCurrentUrl, useCurrentWorkspace } from '@/context/current-workspace';
import { useAnalytics } from '@/services/analytics';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { calc } from '@vanilla-extract/css-utils';
import { useWorkspaceDetails } from '@/features/masquerade-banner';
import { isEmptyString } from '@/utils/string';
import { useProductTaxonomy } from '@/services/product-taxonomy';

type HealthSummaryContentProps = {
  heading: string;
  info: string[];
  hasIssues: boolean;
  severities: { high: number; medium: number };
  healthLink: string;
  onLinkClick: () => void;
};

function HealthSummaryContent({
  heading,
  info,
  hasIssues,
  severities,
  healthLink,
  onLinkClick,
}: HealthSummaryContentProps) {
  return (
    <InlineStack gap="200">
      <HealthScoreGauge size={48} stroke={4} iconSize="md" />
      <Stack gap="100">
        <Heading level="3">{heading}</Heading>
        {info.length > 0
          ? info.map((item) => (
              <Text color="subtle" size="sm" key={item}>
                {item}
              </Text>
            ))
          : null}
        {hasIssues ? (
          <InlineStack gap="100" wrap>
            {severities.high > 0 ? <Badge color="red">{severities.high} high priority</Badge> : null}
            {severities.medium > 0 ? <Badge color="yellow">{severities.medium} medium</Badge> : null}
          </InlineStack>
        ) : null}
        <div>
          <Link href={healthLink} icon="chevron-right" iconPosition="trailing" onClick={onLinkClick}>
            Explore performance
          </Link>
        </div>
      </Stack>
    </InlineStack>
  );
}

export function WorkspaceHealthNotification() {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const { track } = useAnalytics();
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id ?? getWorkspaceIdFromCurrentUrl() ?? 0;

  const { simulate } = useWorkspaceHealthSimulation();
  const healthQuery = useEnvHealth(workspaceId, simulate);
  const workspaceDetailsQuery = useWorkspaceDetails(workspaceId);
  const isIdentifiersMigrationInProgress =
    isEmptyString(workspaceDetailsQuery.data?.identifiers_migration_in_progress) === false;
  const data = healthQuery.data;
  const taxonomy = useProductTaxonomy();

  const { heading, severities, hasIssues, info } = getHealthSummary(
    data,
    isIdentifiersMigrationInProgress,
    taxonomy.term('people.plural'),
  );

  const healthLink = router.buildLocation({ to: '/workspaces/$workspaceId/health', params: { workspaceId } }).href;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      void track('workspaceHealth', 'notificationClicked', {});
    }
    setIsOpen(open);
  };

  // Use the light-check-derived flag from useEnvHealth: off-page, this
  // reflects the fresh /health/check signal, so we hide the banner the
  // moment the env recovers without waiting for heavy.data to refresh.
  // isUnhealthy requires heavy.data to be present, so the popover only
  // appears once getHealthSummary has real data to render.
  if (!healthQuery.isUnhealthy) {
    return null;
  }

  return (
    <Popover isOpen={isOpen} onOpenChange={handleOpenChange} withArrow={false} placement="bottom-end">
      {(context) => (
        <PlainButton
          className={clsx(
            ButtonCss.classNames.button,
            ButtonCss.classNames.buttonVariantTertiary,
            ButtonCss.classNames.buttonToneDefault,
            ButtonCss.classNames.buttonSizeSmall,
          )}
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          aria-label="Workspace health"
        >
          <HealthScoreGauge size={18} stroke={2} showIcon={false} />
        </PlainButton>
      )}

      <PopoverContent style={{ width: calc(themeVars.space['2400']).multiply(2).toString() }}>
        <HealthSummaryContent
          heading={heading}
          info={info}
          hasIssues={hasIssues}
          severities={severities}
          healthLink={healthLink}
          onLinkClick={() => {
            void track('workspaceHealth', 'notificationLinkClicked', {});
            setIsOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
